import "./polyfills";
import pdfWorkerUrl from "pdfjs-dist/legacy/build/pdf.worker.min.mjs?url";
import type { FC, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Loader } from "components/loader/loader";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import { useMount } from "../../helpers/reactHooks";
import styles from "./pdfViewer.module.scss";

type RenderState = "loading" | "ready";
type PdfViewerProps = {
  fileUrl: string;
  documentClassName?: string;
  pageClassName?: string;
};

// Legacy build: supports older Safari
pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

// Module-level so react-pdf doesn't reload the document on re-render
const documentOptions = { isEvalSupported: false };

export const PdfViewer: FC<PdfViewerProps> = ({ fileUrl, documentClassName, pageClassName }) => {
  const [pageWidth, updateWidth] = useState(0);
  const [renderState, setRenderState] = useState<RenderState>("loading");
  const [pagesCount, setPagesCount] = useState(0);
  const documentRef = useRef<HTMLDivElement>(null);

  useMount(() => {
    setRenderState("ready");
  });

  useEffect(() => {
    if (!documentRef.current) {
      return;
    }
    const { width } = documentRef.current.getBoundingClientRect();
    if (pageWidth !== width) {
      updateWidth(width);
    }
  }, [documentRef.current]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }): void => {
    setPagesCount(numPages);
  };

  const renderDocumentContent = (): ReactNode =>
    Array.from(new Array(pagesCount), (_, index) => (
      <Page
        key={`page_${index + 1}`}
        pageNumber={index + 1}
        className={pageClassName || styles.page}
        width={pageWidth}
      />
    ));

  return (
    <div className={documentClassName || styles.document} ref={documentRef}>
      {renderState === "ready" && (
        <Document
          key={fileUrl}
          file={fileUrl}
          options={documentOptions}
          onLoadSuccess={onDocumentLoadSuccess}
          className={documentClassName || styles.document}
          loading={<Loader />}
        >
          {renderDocumentContent()}
        </Document>
      )}
    </div>
  );
};
